// Calendar integration utilities for Google Calendar and Apple Calendar (.ics)

interface CalendarEvent {
  title: string;
  description: string;
  location: string;
  startDate: string; // ISO format or parseable date string
  endDate: string;
  url?: string;
}

function formatDateForGoogle(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function formatDateForICS(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

export function getGoogleCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatDateForGoogle(event.startDate)}/${formatDateForGoogle(event.endDate)}`,
    details: event.description + (event.url ? `\n\nMore info: ${event.url}` : ''),
    location: `${event.location}, Roseville, CA`,
    sf: 'true',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function generateICSFile(event: CalendarEvent): string {
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const uid = `${Date.now()}-${Math.random().toString(36).substring(2)}@roseville.org`;

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Roseville.org//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${formatDateForICS(event.startDate)}`,
    `DTEND:${formatDateForICS(event.endDate)}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
    `LOCATION:${event.location}\\, Roseville\\, CA`,
    event.url ? `URL:${event.url}` : '',
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');

  return icsContent;
}

export function downloadICSFile(event: CalendarEvent): void {
  const icsContent = generateICSFile(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Helper to build calendar event data from our mock event structure
export function buildCalendarEvent(
  event: { title: string; month?: string; day?: string; location: string; time: string },
  details: { fullDescription?: string; website?: string }
): CalendarEvent {
  const year = 2026;
  const monthMap: Record<string, number> = {
    JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
    JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
  };

  // Safety check: if month is missing, use current month
  const currentDate = new Date();
  const monthIndex = event.month ? (monthMap[event.month.toUpperCase()] ?? currentDate.getMonth()) : currentDate.getMonth();
  const dayNum = event.day ? parseInt(event.day) : currentDate.getDate();

  // Parse time range like "6:00 PM - 8:00 PM" or "Every Tuesday, 5:00 PM - 9:00 PM"
  const timeMatch = event.time.match(/(\d{1,2}:\d{2}\s*[AP]M)\s*-\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
  let startHour = 10, startMin = 0, endHour = 18, endMin = 0;

  if (timeMatch) {
    const parseTime = (t: string) => {
      const [timePart, period] = t.trim().split(/\s+/);
      const [h, m] = timePart.split(':').map(Number);
      let hour = h;
      if (period?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
      if (period?.toUpperCase() === 'AM' && hour === 12) hour = 0;
      return { hour, min: m };
    };
    const start = parseTime(timeMatch[1]);
    const end = parseTime(timeMatch[2]);
    startHour = start.hour;
    startMin = start.min;
    endHour = end.hour;
    endMin = end.min;
  }

  const startDate = new Date(year, monthIndex, dayNum, startHour, startMin);
  const endDate = new Date(year, monthIndex, dayNum, endHour, endMin);

  // Safety check for fullDescription
  const description = details.fullDescription 
    ? details.fullDescription.substring(0, 500)
    : `${event.title} at ${event.location}. Time: ${event.time}`;

  return {
    title: event.title,
    description,
    location: event.location,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    url: details.website || '',
  };
}
